/**
 * UUIDv7 — time-sortable UUIDs.
 *
 * Format: 48 bits unix-millis | 4 bits version (7) | 12 bits rand_a |
 *         2 bits variant (10) | 62 bits rand_b
 *
 * No dependencies — uses crypto.getRandomValues which exists on
 * modern Node, browsers, and Cloudflare Workers.
 */

import type { Uuid } from '../types/common.js';

// Web Crypto API — available globally on modern Node, browsers, and
// Cloudflare Workers without requiring any DOM or Workers lib.
declare const crypto: { getRandomValues<T extends ArrayBufferView>(array: T): T };

export function uuidv7(now: Date = new Date()): Uuid {
  const ms = BigInt(now.getTime());
  const rand = new Uint8Array(10);
  crypto.getRandomValues(rand);

  const bytes = new Uint8Array(16);

  // 48 bits unix millis
  bytes[0] = Number((ms >> 40n) & 0xffn);
  bytes[1] = Number((ms >> 32n) & 0xffn);
  bytes[2] = Number((ms >> 24n) & 0xffn);
  bytes[3] = Number((ms >> 16n) & 0xffn);
  bytes[4] = Number((ms >> 8n) & 0xffn);
  bytes[5] = Number(ms & 0xffn);

  // 4-bit version (7) + 12-bit rand_a
  bytes[6] = 0x70 | (rand[0]! & 0x0f);
  bytes[7] = rand[1]!;

  // 2-bit variant (10) + 62-bit rand_b
  bytes[8] = 0x80 | (rand[2]! & 0x3f);
  bytes[9] = rand[3]!;
  bytes[10] = rand[4]!;
  bytes[11] = rand[5]!;
  bytes[12] = rand[6]!;
  bytes[13] = rand[7]!;
  bytes[14] = rand[8]!;
  bytes[15] = rand[9]!;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}` as Uuid;
}
